import React, { useCallback, useEffect, useMemo, useState } from "react";

const DEV_DEBUG_HOSTS = [
    "pokemoncrm.madxpanel.com",
    "localhost",
    "127.0.0.1",
];

const isDebugEnabled = () => {
    if (typeof window === "undefined") return false;
    const params = new URLSearchParams(window.location.search || "");
    const host = window.location.hostname || "";
    return params.get("debug_wallet") === "1" && DEV_DEBUG_HOSTS.includes(host);
};

const safeBool = (value) => (value ? "yes" : "no");

const serializeError = (error) => JSON.stringify({
    message: error?.message || String(error || ""),
    code: error?.code,
    stack: error?.stack,
}, null, 2);

function describeProvider(name, provider) {
    if (!provider) return null;
    return {
        name,
        exists: true,
        hasRequest: typeof provider.request === "function",
        isTrust: !!provider.isTrust,
        isTrustWallet: !!provider.isTrustWallet,
        isTrustWalletProvider: !!provider.isTrustWalletProvider,
        isTrustWalletBrowser: !!provider.isTrustWalletBrowser,
        isMetaMask: !!provider.isMetaMask,
        isTokenPocket: !!provider.isTokenPocket || !!provider.isTokenpocket,
        providerName: provider.name || provider?.info?.name || provider?.providerInfo?.name || "",
        rdns: provider.rdns || provider?.info?.rdns || provider?.providerInfo?.rdns || "",
        constructorName: provider?.constructor?.name || "",
    };
}

function getProviderCandidates() {
    if (typeof window === "undefined") return [];

    const list = [];
    const seen = new Set();

    const push = (name, provider) => {
        if (!provider) return;
        const item = describeProvider(name, provider);
        if (!item) return;
        const key = `${name}:${item.providerName}:${item.rdns}:${item.constructorName}`;
        if (seen.has(key)) return;
        seen.add(key);
        list.push(item);
    };

    push("window.ethereum", window.ethereum);
    push("window.trustwallet", window.trustwallet);
    push("window.trustwallet.ethereum", window.trustwallet?.ethereum);
    push("window.trustWallet", window.trustWallet);
    push("window.trustWallet.ethereum", window.trustWallet?.ethereum);
    push("window.tokenpocket", window.tokenpocket);
    push("window.tokenpocket.ethereum", window.tokenpocket?.ethereum);
    push("window.TokenPocket", window.TokenPocket);
    push("window.TokenPocket.ethereum", window.TokenPocket?.ethereum);

    if (window.ethereum?.providers?.length) {
        window.ethereum.providers.forEach((provider, index) => {
            push(`window.ethereum.providers[${index}]`, provider);
        });
    }

    return list;
}

function getSelectedProvider() {
    if (typeof window === "undefined") return null;
    return (
        window.ethereum ||
        window.trustwallet?.ethereum ||
        window.trustwallet ||
        window.trustWallet?.ethereum ||
        window.trustWallet ||
        window.tokenpocket?.ethereum ||
        window.tokenpocket ||
        window.TokenPocket?.ethereum ||
        window.TokenPocket ||
        null
    );
}

export default function WalletDebugPanel() {
    const [visible, setVisible] = useState(() => isDebugEnabled());
    const [eip6963Providers, setEip6963Providers] = useState([]);
    const [providers, setProviders] = useState([]);
    const [accounts, setAccounts] = useState(null);
    const [chainId, setChainId] = useState(null);
    const [error, setError] = useState("");
    const [tick, setTick] = useState(0);

    const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
    const isMobile = /iphone|ipad|ipod|android/i.test(ua);
    const isTrustWalletUA = /trust\s*wallet|trustwallet/i.test(ua);
    const selectedProvider = getSelectedProvider();
    const selectedProviderInfo = describeProvider("selectedProvider", selectedProvider);

    const trustDeepLink = useMemo(() => {
        if (typeof window === "undefined") return "";
        const target = new URL(window.location.href);
        target.searchParams.set("debug_wallet", "1");
        target.searchParams.set("wallet", "trust");
        target.searchParams.set("tw_browser", "1");
        return `https://link.trustwallet.com/open_url?coin_id=20000714&url=${encodeURIComponent(target.toString())}`;
    }, []);

    const scan = useCallback(async () => {
        try {
            if (typeof window !== "undefined") {
                window.dispatchEvent(new Event("eip6963:requestProvider"));
            }
            setProviders(getProviderCandidates());
            setTick((count) => count + 1);
        } catch (err) {
            setError(serializeError(err));
        }
    }, []);

    const requestAccounts = useCallback(async () => {
        setError("");
        try {
            const provider = getSelectedProvider();
            if (!provider?.request) throw new Error("No provider.request found");
            const result = await provider.request({ method: "eth_requestAccounts" });
            setAccounts(result);
        } catch (err) {
            setError(serializeError(err));
        }
    }, []);

    const getChainId = useCallback(async () => {
        setError("");
        try {
            const provider = getSelectedProvider();
            if (!provider?.request) throw new Error("No provider.request found");
            const result = await provider.request({ method: "eth_chainId" });
            setChainId(result);
        } catch (err) {
            setError(serializeError(err));
        }
    }, []);

    const debugInfo = {
        href: typeof window !== "undefined" ? window.location.href : "",
        host: typeof window !== "undefined" ? window.location.hostname : "",
        userAgent: ua,
        isMobile,
        isTrustWalletUA,
        hasEthereum: typeof window !== "undefined" ? !!window.ethereum : false,
        hasEthereumRequest: typeof window !== "undefined" ? typeof window.ethereum?.request === "function" : false,
        ethereumIsTrust: typeof window !== "undefined" ? !!window.ethereum?.isTrust : false,
        ethereumIsTrustWallet: typeof window !== "undefined" ? !!window.ethereum?.isTrustWallet : false,
        ethereumIsMetaMask: typeof window !== "undefined" ? !!window.ethereum?.isMetaMask : false,
        ethereumProvidersLength: typeof window !== "undefined" ? window.ethereum?.providers?.length || 0 : 0,
        hasTrustwallet: typeof window !== "undefined" ? !!window.trustwallet : false,
        hasTrustWallet: typeof window !== "undefined" ? !!window.trustWallet : false,
        hasTokenPocket: typeof window !== "undefined" ? !!window.tokenpocket || !!window.TokenPocket : false,
        eip6963ProvidersCount: eip6963Providers.length,
        eip6963Providers,
        providers,
        selectedProviderInfo,
        accounts,
        chainId,
        latestError: error,
        walletConnectProjectIdPresent: Boolean(process.env.REACT_APP_REOWN_PROJECT_ID || process.env.REACT_APP_WALLETCONNECT_PROJECT_ID || "configured"),
        scanTick: tick,
    };

    const copyDebugInfo = async () => {
        const text = JSON.stringify(debugInfo, null, 2);
        try {
            if (!navigator.clipboard?.writeText) throw new Error("Clipboard API unavailable");
            await navigator.clipboard.writeText(text);
            window.alert("Wallet debug info copied");
        } catch {
            window.prompt("Copy wallet debug info:", text);
        }
    };

    useEffect(() => {
        if (!visible) return undefined;

        const handler = (event) => {
            const detail = event?.detail || {};
            setEip6963Providers((prev) => {
                const name = detail?.info?.name || detail?.info?.rdns || "unknown";
                const uuid = detail?.info?.uuid || "";
                if (prev.some((provider) => provider.name === name && provider.uuid === uuid)) return prev;
                return [
                    ...prev,
                    {
                        name,
                        rdns: detail?.info?.rdns || "",
                        uuid,
                        hasProvider: !!detail?.provider,
                        hasRequest: typeof detail?.provider?.request === "function",
                    },
                ];
            });
        };

        window.addEventListener("eip6963:announceProvider", handler);
        scan();

        const timer = window.setInterval(scan, 2000);

        return () => {
            window.removeEventListener("eip6963:announceProvider", handler);
            window.clearInterval(timer);
        };
    }, [scan, visible]);

    if (!visible) return null;

    return (
        <div
            style={{
                position: "fixed",
                zIndex: 999999,
                right: 10,
                bottom: 10,
                width: "min(420px, calc(100vw - 20px))",
                maxHeight: "70vh",
                overflow: "auto",
                background: "rgba(0,0,0,0.92)",
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.25)",
                borderRadius: 12,
                padding: 12,
                fontSize: 12,
                fontFamily: "monospace",
                boxShadow: "0 12px 30px rgba(0,0,0,0.45)",
            }}
        >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                <strong>Wallet Debug</strong>
                <button type="button" onClick={() => setVisible(false)}>close</button>
            </div>

            <div style={{ marginTop: 8 }}>
                <div>Mobile: {safeBool(isMobile)}</div>
                <div>Trust UA: {safeBool(isTrustWalletUA)}</div>
                <div>ethereum: {safeBool(debugInfo.hasEthereum)}</div>
                <div>ethereum.request: {safeBool(debugInfo.hasEthereumRequest)}</div>
                <div>ethereum.isTrust: {safeBool(debugInfo.ethereumIsTrust)}</div>
                <div>ethereum.isTrustWallet: {safeBool(debugInfo.ethereumIsTrustWallet)}</div>
                <div>ethereum.isMetaMask: {safeBool(debugInfo.ethereumIsMetaMask)}</div>
                <div>providers: {providers.length}</div>
                <div>eip6963: {eip6963Providers.length}</div>
                <div>selected: {selectedProviderInfo?.name || "-"}</div>
                <div>accounts: {accounts ? JSON.stringify(accounts) : "-"}</div>
                <div>chainId: {chainId || "-"}</div>
            </div>

            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}>
                <button type="button" onClick={scan}>Refresh provider scan</button>
                <button type="button" onClick={requestAccounts}>Request accounts</button>
                <button type="button" onClick={getChainId}>Get chainId</button>
                <button type="button" onClick={copyDebugInfo}>Copy debug info</button>
                <button
                    type="button"
                    onClick={() => {
                        try {
                            window.localStorage.setItem("trustWalletBrowserIntentAt", String(Date.now()));
                        } catch {}
                        window.location.href = trustDeepLink;
                    }}
                >
                    Open Trust Wallet deep link
                </button>
            </div>

            {error ? (
                <pre style={{ color: "#ff8a8a", whiteSpace: "pre-wrap" }}>{error}</pre>
            ) : null}

            <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", marginTop: 10 }}>
                {JSON.stringify(debugInfo, null, 2)}
            </pre>
        </div>
    );
}
